<script setup lang="ts">
import dayjs from "dayjs";
import {computed} from "vue";
import {useModalStore} from "../../store/modal";

const modalStore = useModalStore();
const props = defineProps({
    call: {
        type: Object,
        required: true
    },
    selected: {
        type: Boolean,
        default: false
    },
    showCheckbox: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['select', 'deselect'])

const date = computed(() => props.call.completed_at ?? props.call.created_at)
const type = computed(() => props.call.call_type.replace(/[-_]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))
const client = computed(() => props.call.client_solution.client_contact)
const assign = () => {
    modalStore.open('AssignCall', {id: props.call.id}, 'body', 'bottom-right')
}

const showActions = (props) => {
    modalStore.open("Call", props, `#call-row-${props.id}`, 'center-center')
}

const handleCheckboxChange = (event: Event) => {
    event.preventDefault()
    event.stopPropagation()
    if (props.selected) {
        emit('deselect', props.call.id)
    } else {
        emit('select', props.call.id)
    }
}
</script>

<template>
 <router-link :to="`/calls/view/${call?.id}`" class="call-row-item" :class="{ '--with-checkbox': showCheckbox }" :id="`call-row-${call.id}`">
    <div v-if="showCheckbox" class="call-checkbox" @click.stop="handleCheckboxChange">
        <input type="checkbox" :checked="selected" @change="handleCheckboxChange" @click.stop />
    </div>
    <ah-icon class="call-status-icon" name="call-voice" />
    <dl class="call-timestamp">
        <dt>{{ dayjs(date).format('DD MMM YYYY') }}</dt>
        <dd>{{ dayjs(date).format('HH:mm') }}</dd>
    </dl>

    <dl class="call-meta call-meta-type"><dt>Call type.</dt><dd>
        {{type}}
    </dd></dl>
    <dl class="call-meta call-meta-client-name"><dt>Client Name.</dt><dd>
        {{client.email}} <br>
        {{client.cell_number}}
    </dd></dl>
    <dl class="call-meta call-meta-package"><dt>Package.</dt><dd>
        {{call.client_solution.name}} <br>
        <span class="call-meta-address">{{call?.client_solution?.reseller_order?.cart?.address}}</span>
    </dd></dl>
    <dl class="call-meta call-meta-assignee" v-if="call.assignee"><dt>Assigned to.</dt><dd>
        {{`${call.assignee.first_name} ${call.assignee.last_name[0]}`}}
    </dd></dl>

    <button @click.prevent="assign()" class="call-assign" v-else>
        <span class="label">Assign</span>
        <ah-icon name="user-single-add" />
    </button>

    <div @click.prevent="showActions({id: call.id, assigned: !!call.assignee_id})" class="call-action"><ah-icon name="more-default" /></div>
    <div class="call-navigation"><ah-icon name="arrow-right" /></div>

</router-link>
</template>

<style lang="scss">
    .call-row-item {
        display: grid;
        padding: 0 2rem;
        grid-template-columns: 2.4rem 12rem repeat(2, 1fr) 27.6rem 18rem 3.6rem 3.6rem;
        align-content: center;
        gap: 2rem;

        &.--with-checkbox {
            grid-template-columns: 2.4rem 2.4rem 12rem repeat(2, 1fr) 27.6rem 18rem 3.6rem 3.6rem;
        }
        background: #fff;
        border-radius: 2.4rem;
        height: 8rem;
        color: var(--color-grey-thunder);
        cursor: pointer;
        transition: all .2s ease;
        position: relative;

        > * {
            align-self: center;
            cursor: pointer;
        }

        .call-checkbox {
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;

            input[type="checkbox"] {
                width: 2rem;
                height: 2rem;
                cursor: pointer;
            }
        }

        .call-status-icon {
            fill: var(--color-theme-primary);
            transition: fill .2s ease;
        }

        .call-timestamp {
            background: var(--color-grey-mist);
            height: 6rem;
            border-radius: 1.8rem;
            border: .1rem solid var(--color-grey-dew);
            display: flex;
            justify-content: center;
            align-items: center;
            flex-direction: column;
            transition: all .2s ease;

            dt, dd {
                @include font(1.2, 1.2, black);
            }

            dt {
                margin-bottom: .6rem;
            }
        }

        .call-meta {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;

            dt {
                @include font(1.4, 1.4, black);
                margin-bottom: .6rem;
            }

            dd {
                @include font(1.2, 1.6, book);
                width: 100%;

                .call-meta-address {
                    display: inline-block;
                    max-width: 100%;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
            }

            &.call-meta-assignee {
                padding-left: 2rem;
            }
        }

        .call-status {
            text-transform: capitalize;
            background: var(--color-grey-mist);
            border: .1rem solid var(--color-grey-dew);
            text-align: center;
            @include font(1.1, 3, black);
            border-radius: 1.5rem;
            transition: all .2s ease;
        }

        .call-assign {
            border: none;
            background: var(--color-theme-primary);
            height: 4.4rem;
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 1rem;
            padding: 0 3rem;
            border-radius: 2.2rem;
            transition: background-color .2s ease;

            .label {
                color: #fff;
                font-size: 1.4rem;
                font-weight: var(--font-weight-black);
            }

            .icon {
                fill: #fff;
            }
        }

        .call-action,
        .call-navigation {
            width: 3.6rem;
            height: 3.6rem;
            border-radius: 50%;
            background: transparent;
            fill: var(--color-grey-hazy);
            display: flex;
            justify-content: center;
            align-items: center;
            transition: all .2s ease;
        }

        @include hover() {
            background: var(--color-theme-primary);
            color: #fff;

            .call-status-icon {
                fill: #fff;
            }

            .call-timestamp {
                background: var(--color-black-50);
                border-color: transparent;
            }

            .call-status {
                background: var(--color-black-50);
                border-color: transparent;
            }

            .call-assign {
                background: var(--color-black-50);
            }

            .call-action,
            .call-navigation {
                background: #fff;

                @include hover() {
                    background: var(--color-red-raspberry);
                    fill: #fff;
                }
            }
        }

        @include dark() {
            background-color: var(--color-darth-maul);
            color: #fff;

            .call-timestamp {
                background: var(--color-black-50);
                border-color: transparent;
            }

            .call-status {
                background: var(--color-black-50);
                border-color: transparent;
            }

            .call-action,
            .call-navigation {
                fill: var(--color-grey-dew);
            }

            @include hover() {
                background: var(--color-theme-primary);

                .call-action,
                .call-navigation {
                    background: var(--color-darth-maul);

                    @include hover() {
                        background: var(--color-red-raspberry);
                        fill: #fff;
                    }
                }
            }
        }

        @include mobile() {
            height: auto;
            display: flex;
            flex-wrap: wrap;
            padding: 2rem;

            .call-timestamp {
                width: calc(100% - 4.4rem);
            }

            .call-meta {
                &-type, &-client-name {
                    width: calc(50% - 1rem);
                }
                &-package {
                    padding: 2rem 0;
                    border: solid var(--color-mist);
                    border-width: .1rem 0;
                    dd {
                        .call-meta-address {
                            white-space: unset;
                        }
                    }
                }
                &-assignee {
                    width: 100%;
                    padding-left: 0!important;
                }
            }

            .call-action {
                margin-left: auto;
            }

            .call-assign {
                width: calc(100% - 11.4rem);
            }
        }
    }
</style>
